import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchWorkLogs } from "../api/workLogs";
import { fetchUserReservations } from "../api/auth";
import { useAuth } from "../hooks/useAuth";
import { buildServiceRows, formatRowDate } from "../components/dashboard/volunteerActivity";
import LoadingSpinner from "../components/LoadingSpinner";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import StatusBadge from "../components/history/StatusBadge";
import SourceBadge from "../components/history/SourceBadge";
import MonthSection from "../components/history/MonthSection";
import FilterTabs from "../components/history/FilterTabs";
import SearchBar from "../components/history/SearchBar";
import heroImage from "../assets/Sample/adrien-bruneau-8cpR_Yf0rQs-unsplash.jpg";
import {
    buildMonthSections,
    visibleStatusFilters,
    SOURCE_FILTERS,
    SOURCE_LABELS,
} from "./serviceHistoryUtils";

const COLUMNS = ["Date", "Type", "Activity", "Hours", "Status"];

// Desktop: Date | Type | Activity | Hours | Status.
// Mobile: date + both badges on the first line, name + hours on the second.
function ServiceRow({ row }) {
    const name = row.eventPk ? (
        <Link to={`/event/detail/${row.eventPk}`} className="underline hover:no-underline">
            {row.name}
        </Link>
    ) : (
        row.name
    );

    return (
        <div className="py-[16px] border-b border-[#dfe0e2]">
            <div className="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[130px_90px_1fr_90px_160px] gap-x-4 gap-y-[6px] items-center text-[18px] sm:text-[20px] text-[#5e5e5e]">
                <span className="order-1">{formatRowDate(row.isoDate)}</span>
                <span className="order-2">
                    <SourceBadge source={row.source} />
                </span>
                <span className="order-3 justify-self-end sm:order-5 sm:justify-self-start">
                    <StatusBadge status={row.status} />
                </span>
                <span className="order-4 col-span-2 sm:col-span-1 sm:order-3 min-w-0 break-words">
                    {name}
                </span>
                <span className="order-5 justify-self-end sm:order-4 sm:justify-self-start">
                    {row.hours === null ? "—" : Math.round(row.hours * 10) / 10}
                </span>
            </div>
            {row.rejectionReason && (
                <p className="mt-[7px] sm:ml-[146px] text-[16px] text-[#c52e2e]">
                    Reason: {row.rejectionReason}
                </p>
            )}
        </div>
    );
}

export default function ServiceHistory() {
    const { isAuthenticated, isLoadingUser } = useAuth();
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");

    const [searchParams] = useSearchParams();
    // The Independent Service panel links here with ?source=independent so the
    // page opens scoped to what that panel shows.
    const requestedSource = searchParams.get("source");
    const [sourceFilter, setSourceFilter] = useState(
        SOURCE_FILTERS.includes(requestedSource) ? requestedSource : "all"
    );

    // Router navigation leaves the scroll position alone, so arriving from a
    // link low on another page (Edit Profile's "History & Upcoming") would drop
    // you mid-table. Same mount effect the static pages use.
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    const {
        data: reservations = [],
        isLoading: loadingReservations,
        isError: reservationsError,
    } = useQuery({
        queryKey: ["reservations"],
        queryFn: fetchUserReservations,
        enabled: isAuthenticated,
    });

    const {
        data: workLogs = [],
        isLoading: loadingLogs,
        isError: logsError,
    } = useQuery({
        queryKey: ["workLogs", "volunteer"],
        queryFn: () => fetchWorkLogs("volunteer"),
        enabled: isAuthenticated,
    });

    if (isLoadingUser || loadingReservations || loadingLogs) return <LoadingSpinner />;

    const statusFilters = visibleStatusFilters(sourceFilter);
    // Narrowing the source can hide the active status tab; fall back rather
    // than leaving an invisible filter applied.
    const activeStatus = statusFilters.includes(statusFilter) ? statusFilter : "all";

    const rows = buildServiceRows(reservations, workLogs, new Date());
    const sections = buildMonthSections(rows, {
        status: activeStatus,
        source: sourceFilter,
        search,
    });

    const chooseSource = (source) => {
        setSourceFilter(source);
        if (!visibleStatusFilters(source).includes(statusFilter)) setStatusFilter("all");
    };

    return (
        <div className="bg-white text-left" style={{ fontFamily: "Arial, sans-serif" }}>
            <PageHero image={heroImage} title="Service History" />

            <main className="mt-0 px-6 sm:px-10 xl:px-[83px] pt-[57px] pb-[120px] max-w-[1728px] mx-auto">
                {/* The two tab rows are one block so the search bar can never
                    wrap between them: narrowing the source changes which status
                    tabs exist, so they have to read as one top-down pair. */}
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-5">
                    <div className="flex flex-col gap-y-[18px]">
                        <FilterTabs
                            filters={SOURCE_FILTERS}
                            value={sourceFilter}
                            onChange={chooseSource}
                            labels={SOURCE_LABELS}
                            ariaLabel="Source filter"
                        />
                        <FilterTabs
                            filters={statusFilters}
                            value={activeStatus}
                            onChange={setStatusFilter}
                            ariaLabel="Status filter"
                        />
                    </div>
                    <SearchBar
                        value={search}
                        onChange={setSearch}
                        ariaLabel="Search service history"
                    />
                </div>

                {reservationsError || logsError ? (
                    <p className="mt-[80px] text-[24px] text-[#c52e2e]">
                        Couldn&apos;t load your service history. Please try again later.
                    </p>
                ) : sections.length > 0 ? (
                    sections.map((section) => (
                        <MonthSection
                            key={section.label}
                            label={section.label}
                            approvedHours={section.approvedHours}
                            gridClass="grid-cols-[130px_90px_1fr_90px_160px]"
                            columns={COLUMNS}
                        >
                            {section.items.map((row) => (
                                <ServiceRow key={row.key} row={row} />
                            ))}
                        </MonthSection>
                    ))
                ) : (
                    <p className="mt-[80px] text-[24px] text-[#777f88]">
                        {rows.length === 0 ? "No service history yet." : "No entries match."}
                    </p>
                )}
            </main>

            <Footer />
        </div>
    );
}
